import { MessageSquare } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";

import { useSubmitFeedback } from "../../api/hooks/useSubmitFeedback";
import type { components } from "../../api/schema";
import { appVersion } from "../../lib/appVersion";
import { cn } from "../../lib/cn";
import { captureScreenshot, type CapturedScreenshot } from "../../lib/screenshot";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Textarea } from "../ui/textarea";

type FeedbackCategory = components["schemas"]["FeedbackCategory"];

const CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: "bug", label: "Bug" },
  { value: "idea", label: "Idea or suggestion" },
  { value: "question", label: "Question" },
  { value: "other", label: "Other" },
];

// Mirrors FEEDBACK_MESSAGE_MAX_LENGTH on the server.
const MESSAGE_MAX_LENGTH = 5000;

const SELECT_CLASS =
  "mt-1 w-full rounded-control border border-outline bg-surface-container px-3 py-2 text-sm text-on-surface shadow-sm focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-focus-ring";

function currentPageUrl(): string | null {
  if (typeof window === "undefined") return null;
  return window.location.pathname + window.location.search;
}

function currentUserAgent(): string | null {
  if (typeof navigator === "undefined") return null;
  return navigator.userAgent;
}

// Global "Send feedback" affordance: an icon button in the toolbar that opens
// a dialog with a category + message form. The endpoint is optional-auth, so
// this works for anonymous and authenticated users alike; the page URL, app
// version, and user agent are auto-attached for triage (L0-U6).
//
// `trigger` lets a caller supply its own clickable element (e.g. the sidebar's
// nav-styled "Feedback" button); when omitted the default toolbar icon button
// is used.
//
// The `initial*` props seed the form when the dialog opens, so a caller can hand
// the user a pre-filled report (Unit 1: the import-progress banner's "Report
// this" opens this dialog pre-seeded with the failing batch + error, screenshot
// pre-ticked). They are optional with form-default values, so the floating
// button / sidebar usages are unaffected. The seed is applied on each open
// transition; the screenshot auto-captures then, with the dialog itself excluded
// from the shot (see lib/screenshot IGNORE_SELECTOR) so it captures the page the
// user is reporting on, not the open dialog.
export interface FeedbackDialogProps {
  trigger?: ReactNode;
  initialCategory?: FeedbackCategory;
  initialMessage?: string;
  initialIncludeScreenshot?: boolean;
}

// Outcome of an opt-in screenshot capture, surfaced to the user so they always
// know whether an image will ride along with their report.
type ScreenshotState = "idle" | "capturing" | "ready" | "failed";

export function FeedbackDialog({
  trigger,
  initialCategory = "bug",
  initialMessage = "",
  initialIncludeScreenshot = false,
}: FeedbackDialogProps = {}) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>(initialCategory);
  const [message, setMessage] = useState(initialMessage);
  const [includeScreenshot, setIncludeScreenshot] = useState(initialIncludeScreenshot);
  const [screenshot, setScreenshot] = useState<CapturedScreenshot | null>(null);
  const [screenshotState, setScreenshotState] = useState<ScreenshotState>("idle");
  const submit = useSubmitFeedback();

  const resetScreenshot = () => {
    setIncludeScreenshot(false);
    setScreenshot(null);
    setScreenshotState("idle");
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setCategory(initialCategory);
      setMessage(initialMessage);
      resetScreenshot();
      submit.reset();
    }
  };

  // Capture as soon as the box is ticked (the dialog is excluded from the shot)
  // so the user sees the result before sending. Unticking discards it.
  const handleScreenshotToggle = async (checked: boolean) => {
    setIncludeScreenshot(checked);
    if (!checked) {
      setScreenshot(null);
      setScreenshotState("idle");
      return;
    }
    setScreenshotState("capturing");
    const shot = await captureScreenshot();
    setScreenshot(shot);
    setScreenshotState(shot !== null ? "ready" : "failed");
  };

  // Seed the form from the `initial*` props on each open transition. For a
  // pre-ticked screenshot we capture here (not on a manual tick) so the user
  // sees "Screenshot attached" the moment the pre-filled dialog opens; the
  // dialog is excluded from the shot, so it captures the page behind it.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;
    setCategory(initialCategory);
    setMessage(initialMessage);
    setIncludeScreenshot(initialIncludeScreenshot);
    setScreenshot(null);
    if (!initialIncludeScreenshot) {
      setScreenshotState("idle");
      return;
    }
    setScreenshotState("capturing");
    void captureScreenshot().then((shot) => {
      setScreenshot(shot);
      setScreenshotState(shot !== null ? "ready" : "failed");
    });
  }, [open, initialCategory, initialMessage, initialIncludeScreenshot]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (trimmed.length === 0) return;
    // Attach the pre-captured screenshot when present; a failed/declined
    // capture sends a text-only report (never blocks submission).
    const attach =
      includeScreenshot && screenshot !== null
        ? {
            screenshot_base64: screenshot.base64,
            screenshot_content_type: screenshot.contentType,
          }
        : {};
    submit.mutate({
      category,
      message: trimmed,
      page_url: currentPageUrl(),
      app_version: appVersion(),
      user_agent: currentUserAgent(),
      ...attach,
    });
  };

  const canSubmit =
    message.trim().length > 0 && !submit.isPending && screenshotState !== "capturing";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            aria-label="Send feedback"
            className="inline-flex h-9 w-9 items-center justify-center rounded-control text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
          >
            <MessageSquare aria-hidden="true" className="h-5 w-5" />
          </button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Found a bug or have an idea? Tell us -- it goes straight to the
            team.
          </DialogDescription>
        </DialogHeader>

        {submit.isSuccess ? (
          <div role="status" className="space-y-4">
            <p className="text-sm text-on-surface">
              Thanks for the feedback. We read every report.
            </p>
            <DialogFooter>
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <label className="block">
              <span className="block text-sm font-medium text-on-surface">
                Category
              </span>
              <select
                value={category}
                onChange={(event) =>
                  setCategory(event.target.value as FeedbackCategory)
                }
                className={SELECT_CLASS}
              >
                {CATEGORIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-on-surface">
                Message
              </span>
              <Textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                maxLength={MESSAGE_MAX_LENGTH}
                rows={5}
                required
                placeholder="What happened, or what would make this better?"
                className="mt-1"
              />
            </label>

            <div className="space-y-1">
              <label className="flex items-start gap-2 text-sm text-on-surface">
                <input
                  type="checkbox"
                  checked={includeScreenshot}
                  onChange={(event) => void handleScreenshotToggle(event.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-outline text-primary focus:ring-2 focus:ring-focus-ring"
                />
                <span>
                  Include a screenshot of this page
                  <span className="block text-xs text-on-surface-variant">
                    Helps us see what you saw. This dialog is left out of the shot.
                  </span>
                </span>
              </label>
              {screenshotState === "capturing" ? (
                <p role="status" className="pl-6 text-xs text-on-surface-variant">
                  Capturing screenshot...
                </p>
              ) : null}
              {screenshotState === "ready" ? (
                <p role="status" className="pl-6 text-xs font-medium text-success">
                  Screenshot attached.
                </p>
              ) : null}
              {screenshotState === "failed" ? (
                <p role="status" className="pl-6 text-xs text-on-surface-variant">
                  Couldn't capture a screenshot -- your report will send as text only.
                </p>
              ) : null}
            </div>

            {submit.isError ? (
              <p role="alert" className="text-sm font-medium text-danger">
                Could not send your feedback. Please try again.
              </p>
            ) : null}

            <DialogFooter>
              <Button
                type="submit"
                disabled={!canSubmit}
                className={cn(!canSubmit && "opacity-50")}
              >
                {screenshotState === "capturing"
                  ? "Capturing..."
                  : submit.isPending
                    ? "Sending..."
                    : "Send feedback"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
